import { useState } from "react";
import api from "../api";
import carpet1 from "../assets/carpet_1.avif";

export default function Contact() {
  const heroCarpet = carpet1;

  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");

  const shimmer =
    "bg-[linear-gradient(90deg,#e5e7eb_25%,#f3f4f6_50%,#e5e7eb_75%)] bg-shimmer animate-shimmer";

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSending(true);
    setStatus("");

    try {
      await api.post("/api/contact/", formData);

      setStatus("success");

      setFormData({
        full_name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#f7f1e8] text-gray-800">
      {/* PRELOAD HERO IMAGE */}
      <img
        src={heroCarpet}
        alt=""
        className="hidden"
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />

      {loading ? (
        <>
          {/* HERO SKELETON */}
          <section className={`h-[50vh] ${shimmer}`} />

          {/* CONTENT SKELETON */}
          <section className="max-w-7xl mx-auto px-6 py-24">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <div className={`h-5 w-32 rounded mb-6 ${shimmer}`} />

                <div className={`h-14 w-72 rounded mb-8 ${shimmer}`} />

                <div className={`h-24 w-full rounded mb-10 ${shimmer}`} />

                <div className="space-y-8">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div
                      key={item}
                      className={`h-28 rounded-2xl ${shimmer}`}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-white p-10 rounded-3xl shadow-2xl">
                <div className={`h-10 w-56 rounded mb-8 ${shimmer}`} />

                <div className="space-y-6">
                  {[1, 2, 3].map((item) => (
                    <div key={item}>
                      <div
                        className={`h-5 w-28 rounded mb-3 ${shimmer}`}
                      />

                      <div className={`h-14 rounded-xl ${shimmer}`} />
                    </div>
                  ))}

                  <div>
                    <div
                      className={`h-5 w-28 rounded mb-3 ${shimmer}`}
                    />

                    <div className={`h-32 rounded-xl ${shimmer}`} />
                  </div>

                  <div className={`h-14 rounded-xl ${shimmer}`} />
                </div>
              </div>
            </div>
          </section>

          {/* MAP SKELETON */}
          <section className="pb-24 px-6">
            <div className="max-w-7xl mx-auto">
              <div className={`h-[450px] rounded-3xl ${shimmer}`} />
            </div>
          </section>
        </>
      ) : (
        <>
          {/* =====================================================
              HERO
          ====================================================== */}

          <section className="relative h-[50vh] overflow-hidden">
            <img
              src={heroCarpet}
              alt="A1 Rugs handmade carpet"
              className="absolute inset-0 w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-black/60" />

            <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
              <p className="text-[#C89B3C] uppercase tracking-[6px] mb-4 font-medium">
                A1 Rugs
              </p>

              <h1 className="text-5xl md:text-6xl font-serif text-white font-bold">
                Contact Us
              </h1>

              <div className="w-24 h-1 bg-[#C89B3C] mt-6" />

              <p className="text-white/90 text-lg md:text-xl mt-6 max-w-2xl">
                We are here to help you find the perfect handmade rug for
                your home.
              </p>
            </div>
          </section>

          {/* =====================================================
              CONTACT CONTENT
          ====================================================== */}

          <section className="max-w-7xl mx-auto px-6 py-24">
            <div className="grid lg:grid-cols-2 gap-16">

              {/* COMPANY INFORMATION */}

              <div>
                <p className="text-[#0F5C5E] uppercase tracking-[4px] font-semibold">
                  Let's Talk
                </p>

                <h2 className="text-5xl font-serif text-[#0F5C5E] mt-4 mb-8">
                  We'd Love To Hear From You
                </h2>

                <p className="text-gray-600 text-lg leading-9 mb-10">
                  Whether you are looking for a beautiful handmade Afghan rug,
                  need help choosing the right size, or have a question about
                  an order, the A1 Rugs team is here to help.
                </p>

                <p className="text-gray-600 text-lg leading-9 mb-10">
                  Visit us in Seattle or get in touch with our team by phone,
                  WhatsApp, or email. We are always happy to help you find the
                  right rug for your space.
                </p>

                <div className="space-y-8">

                  {/* ADDRESS */}

                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-[#0F5C5E]">
                    <h3 className="text-2xl font-bold text-[#0F5C5E] mb-2">
                      Address
                    </h3>

                    <p className="text-gray-600">
                      10821 8th Ave
                      <br />
                      Seattle, WA
                    </p>
                  </div>

                  {/* PHONE */}

                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-[#0F5C5E]">
                    <h3 className="text-2xl font-bold text-[#0F5C5E] mb-2">
                      Phone
                    </h3>

                    <div className="space-y-2">
                      <a
                        href="tel:+14259546183"
                        className="block text-gray-600 hover:text-[#0F5C5E] transition"
                      >
                        +1 (425) 954-6183
                      </a>

                      <a
                        href="tel:+93731480444"
                        className="block text-gray-600 hover:text-[#0F5C5E] transition"
                      >
                        +93 731 480 444
                      </a>
                    </div>
                  </div>

                  {/* WHATSAPP */}

                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-[#0F5C5E]">
                    <h3 className="text-2xl font-bold text-[#0F5C5E] mb-2">
                      WhatsApp
                    </h3>

                    <div className="space-y-2">
                      <a
                        href="https://wa.me/14259546183"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-gray-600 hover:text-[#0F5C5E] transition"
                      >
                        +1 (425) 954-6183
                      </a>

                      <a
                        href="https://wa.me/93731480444"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-gray-600 hover:text-[#0F5C5E] transition"
                      >
                        +93 731 480 444
                      </a>
                    </div>
                  </div>

                  {/* EMAIL */}

                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-[#0F5C5E]">
                    <h3 className="text-2xl font-bold text-[#0F5C5E] mb-2">
                      Email
                    </h3>

                    <a
                      href="mailto:info@shopa1rugs.com"
                      className="text-gray-600 hover:text-[#0F5C5E] transition"
                    >
                      info@shopa1rugs.com
                    </a>
                  </div>

                  {/* WHOLESALE */}

                  <div className="bg-white p-7 rounded-2xl shadow-lg border-l-4 border-[#C89B3C]">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                      <h3 className="text-2xl font-bold text-[#0F5C5E]">
                        Wholesale Available
                      </h3>

                      <span className="rounded-full bg-[#0F5C5E] px-4 py-1.5 text-sm font-semibold text-white">
                        Lower Prices
                      </span>
                    </div>

                    <p className="text-gray-600 leading-7">
                      Looking to purchase rugs in larger quantities? We offer
                      wholesale pricing at lower prices for businesses,
                      retailers, interior designers, and bulk buyers.
                    </p>

                    <p className="mt-4 text-gray-600 leading-7">
                      Contact our team to discuss your wholesale requirements,
                      available rugs, quantities, and pricing.
                    </p>

                    <a
                      href="mailto:info@shopa1rugs.com?subject=Wholesale%20Inquiry"
                      className="inline-block mt-5 rounded-full bg-[#0F5C5E] px-6 py-3 font-semibold text-white transition duration-300 hover:bg-[#0a4547]"
                    >
                      Contact Us for Wholesale
                    </a>
                  </div>

                  {/* BUSINESS */}

                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-[#C89B3C]">
                    <h3 className="text-2xl font-bold text-[#0F5C5E] mb-2">
                      A1 Rugs
                    </h3>

                    <p className="text-gray-600 leading-7">
                      Authentic handmade rugs and carpets, bringing timeless
                      craftsmanship and Afghan rug traditions to homes in the
                      United States and beyond.
                    </p>
                  </div>

                </div>
              </div>

              {/* CONTACT FORM */}

              <div className="bg-white p-10 rounded-3xl shadow-2xl h-fit border-t-4 border-[#0F5C5E]">

                <h3 className="text-3xl font-serif text-[#0F5C5E] mb-8">
                  Send Us A Message
                </h3>

                <form
                  className="space-y-6"
                  onSubmit={handleSubmit}
                >

                  {/* FULL NAME */}

                  <div>
                    <label className="block mb-2 font-medium text-gray-800">
                      Full Name
                    </label>

                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      required
                      className="
                        w-full
                        border
                        border-[#d8c8b8]
                        rounded-xl
                        px-5
                        py-4
                        bg-white
                        focus:outline-none
                        focus:border-[#0F5C5E]
                        focus:ring-2
                        focus:ring-[#0F5C5E]/20
                        transition
                      "
                    />
                  </div>

                  {/* EMAIL */}

                  <div>
                    <label className="block mb-2 font-medium text-gray-800">
                      Email Address
                    </label>

                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email"
                      required
                      className="
                        w-full
                        border
                        border-[#d8c8b8]
                        rounded-xl
                        px-5
                        py-4
                        bg-white
                        focus:outline-none
                        focus:border-[#0F5C5E]
                        focus:ring-2
                        focus:ring-[#0F5C5E]/20
                        transition
                      "
                    />
                  </div>

                  {/* PHONE */}

                  <div>
                    <label className="block mb-2 font-medium text-gray-800">
                      Phone Number
                    </label>

                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your Phone"
                      required
                      className="
                        w-full
                        border
                        border-[#d8c8b8]
                        rounded-xl
                        px-5
                        py-4
                        bg-white
                        focus:outline-none
                        focus:border-[#0F5C5E]
                        focus:ring-2
                        focus:ring-[#0F5C5E]/20
                        transition
                      "
                    />
                  </div>

                  {/* MESSAGE */}

                  <div>
                    <label className="block mb-2 font-medium text-gray-800">
                      Message
                    </label>

                    <textarea
                      rows="5"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Write your message..."
                      required
                      className="
                        w-full
                        border
                        border-[#d8c8b8]
                        rounded-xl
                        px-5
                        py-4
                        bg-white
                        resize-none
                        focus:outline-none
                        focus:border-[#0F5C5E]
                        focus:ring-2
                        focus:ring-[#0F5C5E]/20
                        transition
                      "
                    />
                  </div>

                  {/* SUBMIT */}

                  <button
                    type="submit"
                    disabled={sending}
                    className="
                      w-full
                      bg-[#0F5C5E]
                      text-white
                      py-4
                      rounded-xl
                      font-semibold
                      text-lg
                      hover:bg-[#0a4547]
                      transition
                      disabled:opacity-60
                      disabled:cursor-not-allowed
                    "
                  >
                    {sending ? "Sending..." : "Send Message"}
                  </button>

                  {/* SUCCESS */}

                  {status === "success" && (
                    <p className="text-green-600 text-center mt-4">
                      Message sent successfully! We will get back to you soon.
                    </p>
                  )}

                  {/* ERROR */}

                  {status === "error" && (
                    <p className="text-red-600 text-center mt-4">
                      Failed to send your message. Please try again.
                    </p>
                  )}

                </form>
              </div>
            </div>
          </section>

          {/* =====================================================
              SEATTLE MAP
          ====================================================== */}

          <section className="pb-24 px-6">
            <div className="max-w-7xl mx-auto">

              <div className="mb-8 text-center">
                <p className="text-[#0F5C5E] uppercase tracking-[4px] font-semibold">
                  Find Us
                </p>

                <h2 className="text-4xl font-serif text-[#0F5C5E] mt-3">
                  Our Location
                </h2>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-2xl border border-[#d8c8b8]">
                <iframe
                  title="A1 Rugs Seattle Location"
                  src="https://maps.google.com/maps?q=10821%208th%20Ave%2C%20Seattle%2C%20WA&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-[450px]"
                  loading="lazy"
                  allowFullScreen
                />
              </div>

            </div>
          </section>
        </>
      )}
    </div>
  );
}